import React, { useState, useRef } from 'react';
import axios from 'axios';
import endpoints from '../../define/endpoints';
import ModalDrawer from './ModalDrawer';
import { useForm } from 'react-hook-form';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { Timeline } from '../Timeline/Timeline';
import { TRANSLATIONS } from '../../../translations';
import { RadioList } from './RadioList';
import { BetaTextInput } from '../BetaTextInput';
import { Loader } from '../common';
import Typography, {
  TypographyStates,
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';

const SUCCESS_ICON = 'circleCheck';
const FAIL_ICON = 'warning';
const CANCELLATION_REASON = 'cancellation_reason';
const CUSTOM_REASON = 'custom_reason';
const REASON = 'reason';

const ModalCancelVoucherForm = ({
  isOpen,
  setIsOpen,
  goBack,
  token,
  orderId,
  isDeviceSmallerThanMblLandscape,
  setRequestDetails,
}) => {
  const [showResponse, setShowResponse] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [responseContent, setResponseContent] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [radioListError, setRadioListError] = useState(null);
  const [motivationError, setMotivationError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, setValue, watch } = useForm();

  const formRef = useRef(null);
  const reasonslist = {
    cancellation_reason_1:
      TRANSLATIONS.RETHINKING_OR_CHANGING_PREFERENCES_ABOUT_THE_ACTIVITY,
    cancellation_reason_2: TRANSLATIONS.ERROR_IN_VOUCHER_PURCHASE,
    cancellation_reason_3:
      TRANSLATIONS.GIFT_NO_LONGER_SUITABLE_FOR_THE_RECIPIENT,
    cancellation_reason_4: TRANSLATIONS.PERSONAL_UNFORESEEN_EVENTS,
    cancellation_reason_5:
      TRANSLATIONS.HEALTH_PROBLEMS_OR_PHYSICAL_LIMITATIONS_ARISEN,
    cancellation_reason_6: TRANSLATIONS.A_MORE_ADVANTAGEOUS_OFFER_FOUND,
    other: TRANSLATIONS.OTHER,
  };

  const onSubmit = async (data, event) => {
    event.preventDefault();
    const reason = data.reason || '';

    if ((!reason || reason === '') && !showCustomInput) {
      setRadioListError(TRANSLATIONS.RADIO_LIST_ERROR);
      return;
    } else if ((!reason || reason === '') && showCustomInput) {
      setMotivationError(TRANSLATIONS.MOTIVATION_ERROR);
      return;
    }

    try {
      setIsLoading(true);
      const { data } = await axios.post(
        `${endpoints.core}/order-customer-request/store`,
        {
          order_id: orderId,
          type: 'voucher_withdraw',
          reason: reason,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let requestDetails = data && data.order ? data.order : null;
      setRequestDetails(requestDetails);

      setIsSuccess(true);
      setResponseContent(
        TRANSLATIONS.PARTNER_WILL_BE_NOTIFIED_AND_WILL_EVALUATE_REQUEST
      );
    } catch (error) {
      setResponseContent(
        TRANSLATIONS.UNEXPECTED_ERROR_HAS_OCCURRED_PLEASE_RETRY_LATER
      );
    } finally {
      setShowResponse(true);
      setIsOpen(false);
      setIsLoading(false);
    }
  };

  const timelineSteps = [
    {
      icon: 'message',
      status: 'approved',
      title: {
        text: TRANSLATIONS.SENDING_REQUEST,
        bold: true,
      },
    },
    {
      icon: 'spinner-dotted',
      status: 'approved',
      title: {
        text: TRANSLATIONS.PROCESSING,
        bold: true,
      },
    },
    {
      icon: 'document',
      status: 'pending',
      title: {
        text: TRANSLATIONS.OUTCOME,
        bold: true,
      },
    },
  ];
  const responseModalDrawer = (
    <ModalDrawer
      isOpen={showResponse}
      setIsOpen={setShowResponse}
      isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
      modalCustomWidth={640}
      title={TRANSLATIONS.ACTIVITY_CANCEL_REQUEST}
      content={
        <div className="pl-m pr-m pt-xl pb-xl w-100%">
          <div className="d-flex flex-column items-center pb-2xs gap-xs">
            <BetaIcon
              name={isSuccess ? SUCCESS_ICON : FAIL_ICON}
              color={IconColors.regular}
              size={IconSizes[64]}
            />
          </div>
          {isSuccess && (
            <div className="w-100% text-center pb-2xl">
              <div className="w-100% text-center pb-xl">
                <Typography
                  variant={TypographyVariants.p2}
                  weight={TypographyWeights.bold}
                >
                  {
                    TRANSLATIONS.CANCELLATION_REQUEST_HAS_BEEN_SUCCESSFULLY_SUBMITTED
                  }
                </Typography>
              </div>
              <Timeline timelineSteps={timelineSteps} />
            </div>
          )}
          <div className="d-flex text-center align-center">
            <div
              className="typography__p2 typography__neutral text-center"
              dangerouslySetInnerHTML={{ __html: responseContent }}
            />
          </div>
        </div>
      }
      footer={
        <div className="d-flex justify-center gap-m pb-m pl-xl pr-xl">
          <Button
            classNames={'pr-m pl-m'}
            size={ButtonSizes.s}
            style={ButtonStyles.empty}
            label={<b>{TRANSLATIONS.CLOSE}</b>}
            onClick={() => setShowResponse(false)}
          />
        </div>
      }
    />
  );

  const form = (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <div className="pb-2xs">
        <RadioList
          inputName={CANCELLATION_REASON}
          options={reasonslist}
          defaultValue={null}
          onChange={(value) => {
            setValue(CANCELLATION_REASON, value);
            if (value === 'other') {
              setShowCustomInput(true);
              setValue(REASON, '');
            } else {
              setShowCustomInput(false);
              setValue(REASON, reasonslist[value]);
            }
          }}
          oneColumn={true}
          error={radioListError}
          setError={setRadioListError}
        />
      </div>

      {showCustomInput && (
        <>
          <BetaTextInput
            asTextArea
            className="modal-ord-li-review__review-form__field"
            label={TRANSLATIONS.INSERT_MESSAGE}
            name={CUSTOM_REASON}
            setRef={register({ required: false, maxLength: { value: 500 } })}
            defaultValue={''}
            onChange={(event) => {
              // const value = event.target.value.replace(
              //   /[^a-zA-Z0-9\s.,!?'-]/g,
              //   ''
              // );
              const value = event.target.value;
              setValue(REASON, value);
              setValue(CUSTOM_REASON, value);
            }}
            resize={true}
            limit={500}
            error={motivationError}
            setError={setMotivationError}
            errorMessage={TRANSLATIONS.MOTIVATION_ERROR}
          />
        </>
      )}
      <input type="hidden" name="reason" ref={register} />
    </form>
  );

  const resetErrors = () => {
    setTimeout(() => {
      setRadioListError(null);
      setMotivationError(null);
    }, 500);
  };

  return (
    <>
      {responseModalDrawer}
      <ModalDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        goBack={() => goBack()}
        isDeviceSmallerThanTablet={isDeviceSmallerThanMblLandscape}
        modalCustomWidth={640}
        title={TRANSLATIONS.ACTIVITY_CANCEL_REQUEST}
        content={
          <div className="pl-m pr-m pt-xl pb-xl w-100% d-flex flex-column gap-xl">
            {isLoading ? (
              <Loader loading={isLoading} />
            ) : (
              <>
                <div>
                  <Typography
                    classNames="text-left"
                    variant={TypographyVariants.p2}
                  >
                    <b>
                      {TRANSLATIONS.TELL_WHY_YOU_WANT_TO_CANCEL_YOUR_VOUCHER}
                    </b>
                  </Typography>
                  <Typography
                    classNames="text-left"
                    variant={TypographyVariants.p3}
                  >
                    {
                      TRANSLATIONS.THESE_INFORMATIONS_HELP_US_IMPROVE_OUR_SERVICE
                    }
                  </Typography>
                </div>
                {form}
              </>
            )}
          </div>
        }
        footer={
          <div className="d-flex gap-m pb-m pl-xl pr-xl">
            <Button
              classNames={'flex-1 pr-m pl-m'}
              size={ButtonSizes.s}
              style={ButtonStyles.empty}
              label={TRANSLATIONS.CANCEL}
              disabled={isLoading}
              onClick={() => {
                setIsOpen(false);
                resetErrors();
              }}
            />
            <Button
              classNames={'flex-1 pr-m pl-m'}
              size={ButtonSizes.s}
              style={ButtonStyles.secondary}
              label={TRANSLATIONS.CONFIRM}
              disabled={isLoading}
              onClick={() => {
                formRef.current.dispatchEvent(
                  new Event('submit', { cancelable: true })
                );
              }}
            />
          </div>
        }
        handleAfterClose={resetErrors}
      />
    </>
  );
};

ModalCancelVoucherForm.propTypes = {};

export { ModalCancelVoucherForm };
